/**
 * Prowizje — pulpit, ranking, potrącenia, dokumenty i profil handlowca — klucze `comov.*`.
 * Scalane w słownik przez `extra.ts`; `comOvEn` jest typowane względem `comOvPl`, więc brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const comOvPl = {
  tabs: { overview: 'Przegląd', settlement: 'Rozliczenie miesięczne', documents: 'Dokumenty' },
  range: { m3: '3 mies.', m6: '6 mies.', m12: '12 mies.', ytd: 'Od początku roku', custom: 'Własny okres', label: 'Okres' },
  compare: 'vs {from} – {to}',
  inProgress: 'w toku',
  export: { label: 'Excel', ranking: 'Ranking handlowców (okres)', documents: 'Dokumenty miesiąca' },
  hero: {
    payable: 'Do wypłaty',
    payableHint: '{n} dokumentów · {reps} handlowców',
    accrued: 'Naliczona',
    accruedHint: 'od {net} netto',
    lost: 'Przepadło przez spóźnienia',
    lostHint: '{pct} naliczonej prowizji',
    lostNone: 'nic nie przepadło',
    rate: 'Stawka efektywna',
    rateHint: 'naliczona / netto objęte',
    onTime: 'Zapłacone w terminie',
    onTimeHint: 'spóźnione średnio {days} dni',
    onTimeHintNone: 'brak spóźnień',
  },
  trend: {
    title: 'Prowizja miesiąc po miesiącu',
    subtitle: 'Do wypłaty, potrącenia za spóźnione zapłaty i stawka efektywna. Kliknij miesiąc, aby otworzyć rozliczenie.',
    payable: 'Do wypłaty',
    penalty: 'Potrącenia',
    rate: 'Stawka efektywna',
    onTime: 'W terminie',
    projection: 'Tempo miesiąca',
  },
  insights: {
    title: 'Najważniejsze',
    subtitle: 'Wnioski z danych okresu',
    empty: 'Brak prowizji w okresie — nie ma z czego wyciągać wniosków.',
    growth: 'Do wypłaty {value} — {delta} względem poprzedniego okresu.',
    decline: 'Do wypłaty {value} — spadek o {delta} względem poprzedniego okresu.',
    flat: 'Do wypłaty {value} — tyle samo co w poprzednim okresie.',
    lost: 'Przez spóźnione zapłaty klientów przepadło {value} prowizji ({pct} naliczonej).',
    lostTop: 'Najwięcej kosztował klient {customer}: {value} potrąceń.',
    leader: 'Liderem jest {rep}: {value}, czyli {share} wszystkich prowizji.',
    punctuality: 'Najsłabsza terminowość: {rep} — {pct} dokumentów zapłaconych w terminie.',
    projection: 'Bieżący miesiąc w toku: {value} po {days} dniach, w tym tempie ok. {projected}.',
    bestGroup: 'Najwięcej prowizji daje grupa {group}: {value} przy stawce {rate}.',
  },
  ranking: {
    title: 'Ranking handlowców',
    subtitle: 'Kliknij handlowca, aby otworzyć jego profil prowizji.',
    rep: 'Handlowiec',
    payable: 'Do wypłaty',
    change: 'Zmiana',
    rate: 'Stawka ef.',
    onTime: 'W terminie',
    lost: 'Przepadło',
    customers: 'Klienci',
    trend: 'Trend',
    share: '{pct} całości',
    empty: 'Brak handlowców z prowizją w okresie.',
    new: 'nowy',
  },
  late: {
    title: 'Gdzie przepada prowizja',
    subtitle: 'Dokumenty wg dni od terminu do zapłaty i potrącenia, jakie z tego wyszły.',
    buckets: { onTime: 'W terminie', d1_14: '1–14 dni', d15_30: '15–30 dni', d31_60: '31–60 dni', d61_90: '61–90 dni', d91: 'Ponad 90 dni' },
    docs: '{n} dok.',
    penalty: 'Potrącenia',
    customersTitle: 'Klienci, przez których przepadło najwięcej',
    customersEmpty: 'Żaden klient nie spowodował potrąceń w tym okresie.',
    avgLate: 'śr. {days} dni po terminie',
    collections: 'Windykacja',
  },
  groups: {
    title: 'Prowizja wg grup towarów',
    subtitle: 'Skąd bierze się prowizja i jaka jest stawka efektywna w każdej grupie.',
    rest: 'Pozostałe grupy',
    noGroup: 'bez grupy',
    rate: 'stawka {rate}',
    empty: 'Brak pozycji z prowizją w okresie.',
  },
  customers: {
    title: 'Klienci z największą prowizją',
    subtitle: 'Do wypłaty w okresie, z handlowcami, którym naliczyli prowizję.',
    empty: 'Brak klientów z prowizją w okresie.',
    docs: '{n} dok.',
  },
  profile: {
    title: 'Profil prowizji',
    months: 'Miesiące',
    monthsHint: 'Kliknij miesiąc, aby zobaczyć dokumenty.',
    customers: 'Najwięksi klienci',
    lostCustomers: 'Klienci z potrąceniami',
    share: 'Udział w prowizji zespołu',
    rank: 'Miejsce w rankingu: {rank} z {of}',
    documents: 'Dokumenty',
  },
  docs: {
    title: 'Dokumenty z prowizją',
    subtitle: 'Wszystkie dokumenty miesiąca w Twoim zakresie danych — rozwiń dokument, aby zobaczyć grupy towarów i stawki.',
    month: 'Miesiąc',
    search: 'Szukaj dokumentu lub klienta',
    lateOnly: 'Tylko po terminie',
    sort: { label: 'Sortuj', payable: 'Do wypłaty', daysLate: 'Dni po terminie', penalty: 'Potrącenie', net: 'Netto' },
    summary: '{n} dokumentów · do wypłaty {payable} · potrącenia {penalty}',
    empty: 'Brak dokumentów spełniających warunki.',
  },
};

export const comOvEn: Shape<typeof comOvPl> = {
  tabs: { overview: 'Overview', settlement: 'Monthly settlement', documents: 'Documents' },
  range: { m3: '3 mo.', m6: '6 mo.', m12: '12 mo.', ytd: 'Year to date', custom: 'Custom period', label: 'Period' },
  compare: 'vs {from} – {to}',
  inProgress: 'in progress',
  export: { label: 'Excel', ranking: 'Rep ranking (period)', documents: 'Documents of the month' },
  hero: {
    payable: 'To pay',
    payableHint: '{n} documents · {reps} reps',
    accrued: 'Accrued',
    accruedHint: 'on {net} net',
    lost: 'Lost to late payments',
    lostHint: '{pct} of the accrued commission',
    lostNone: 'nothing lost',
    rate: 'Effective rate',
    rateHint: 'accrued / net covered',
    onTime: 'Paid on time',
    onTimeHint: 'late ones {days} days on average',
    onTimeHintNone: 'no late payments',
  },
  trend: {
    title: 'Commission month by month',
    subtitle: 'To pay, late-payment deductions and the effective rate. Click a month to open its settlement.',
    payable: 'To pay',
    penalty: 'Deductions',
    rate: 'Effective rate',
    onTime: 'On time',
    projection: 'Month pace',
  },
  insights: {
    title: 'Key points',
    subtitle: 'Conclusions from the period',
    empty: 'No commissions in the period — nothing to conclude.',
    growth: 'To pay {value} — {delta} against the previous period.',
    decline: 'To pay {value} — down {delta} against the previous period.',
    flat: 'To pay {value} — the same as in the previous period.',
    lost: 'Late customer payments cost {value} of commission ({pct} of the accrued).',
    lostTop: 'The most expensive customer was {customer}: {value} of deductions.',
    leader: 'The leader is {rep}: {value}, i.e. {share} of all commissions.',
    punctuality: 'Weakest punctuality: {rep} — {pct} of documents paid on time.',
    projection: 'Current month in progress: {value} after {days} days, about {projected} at this pace.',
    bestGroup: 'The top product group is {group}: {value} at a {rate} rate.',
  },
  ranking: {
    title: 'Rep ranking',
    subtitle: 'Click a rep to open their commission profile.',
    rep: 'Sales rep',
    payable: 'To pay',
    change: 'Change',
    rate: 'Eff. rate',
    onTime: 'On time',
    lost: 'Lost',
    customers: 'Customers',
    trend: 'Trend',
    share: '{pct} of total',
    empty: 'No reps with commission in the period.',
    new: 'new',
  },
  late: {
    title: 'Where commission is lost',
    subtitle: 'Documents by days from the due date to payment and the deductions that followed.',
    buckets: { onTime: 'On time', d1_14: '1–14 days', d15_30: '15–30 days', d31_60: '31–60 days', d61_90: '61–90 days', d91: 'Over 90 days' },
    docs: '{n} docs',
    penalty: 'Deductions',
    customersTitle: 'Customers who cost the most',
    customersEmpty: 'No customer caused deductions in this period.',
    avgLate: 'avg. {days} days late',
    collections: 'Collections',
  },
  groups: {
    title: 'Commission by product group',
    subtitle: 'Where the commission comes from and the effective rate of each group.',
    rest: 'Other groups',
    noGroup: 'no group',
    rate: 'rate {rate}',
    empty: 'No commissioned lines in the period.',
  },
  customers: {
    title: 'Customers with the largest commission',
    subtitle: 'To pay in the period, with the reps they earned commission for.',
    empty: 'No customers with commission in the period.',
    docs: '{n} docs',
  },
  profile: {
    title: 'Commission profile',
    months: 'Months',
    monthsHint: 'Click a month to see its documents.',
    customers: 'Largest customers',
    lostCustomers: 'Customers with deductions',
    share: 'Share of the team commission',
    rank: 'Rank: {rank} of {of}',
    documents: 'Documents',
  },
  docs: {
    title: 'Commissioned documents',
    subtitle: 'All documents of the month in your data scope — expand a document to see its product groups and rates.',
    month: 'Month',
    search: 'Search document or customer',
    lateOnly: 'Late only',
    sort: { label: 'Sort', payable: 'To pay', daysLate: 'Days late', penalty: 'Deduction', net: 'Net' },
    summary: '{n} documents · to pay {payable} · deductions {penalty}',
    empty: 'No documents match.',
  },
};
